import React from 'react';
import cn from 'classnames';

import { ibeAccountManagerRead } from 'actions/salon/beAccountManager.action';
import NO_IMAGE from 'assets/images/online-booking/no-image-available.png';
import IMG_BUTTON from 'assets/images/support/button-arrow.png';
import IMG_ENVELOPE from 'assets/images/support/envelope-solid.svg';
import IMG_HELP_CENTER from 'assets/images/support/help-center.webp';
import IMG_CUSTOMER from 'assets/images/support/ico-customer.png';
import IMG_MANAGEMENT from 'assets/images/support/ico-management.png';
import IMG_TIPS from 'assets/images/support/ico-tips.png';
import IMG_PHONE from 'assets/images/support/phone-solid.svg';
import IMG_PLAN from 'assets/images/support/plan-illustration.png';
import IMG_QR_CODE from 'assets/images/support/qr-code.png';
import IMG_TEAM from 'assets/images/support/team.jpg';
import Showcase from 'components/account/component/Showcase.component';
import Title from 'components/account/component/Title.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import Space from 'components/generic/Space.component';
import Video from 'components/generic/Video.component';
import SidebarMenu from 'components/menu/SidebarMenu.component';
import S from 'constants/string.const';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import useWindowScroll from 'hooks/useWindowScroll.hook';
import fromAnyToString from 'utils/converters/fromAnyToString';

import TopNav from 'common/TopNav.component';

import CN from './SupportPage.module.scss';

const LINK_MAIL = 'mailto:support@getslick.com';
const LINK_PHONE = 'tel:01202286005';
const LINK_ROADMAP = 'https://slick.sleekplan.app/roadmap';
const LINK_PLAN = 'https://slick.sleekplan.app/';
const LINK_WHATSAPP = 'https://api.whatsapp.com/message/4S4PTWAYL7IEB1?autoload=1&app_absent=0';
const LINK_VIDEO = 'https://www.youtube.com/embed/lgavPTDV6b0?si=eWIcgc4-NYmtVfkM';
const TITLE_VIDEO = 'A message from us';

const RENDER_VIDEO = false;

const SupportPage = () => {
  useWindowScroll(0, 0);

  const { qj } = usePrepareReadOnly(ibeAccountManagerRead);

  const name = fromAnyToString(qj?.name) || 'No Assigned';
  const image = qj?.profileImageUrl || NO_IMAGE;
  const link = fromAnyToString(qj?.link);

  const renderManagerSection = !!(name && link);

  return (
    <div data-bem="SupportPage" className={CN.component}>
      <TopNav title="Support & Account Management">
        <SidebarMenu />
      </TopNav>

      <div className={CN.wrap}>
        <div data-bem="SupportPage__hero-banner" className={CN.heroBanner}>
          <div className={CN.bannerHeaderContainer}>
            <div className={CN.topBannerTitle}>Support & Account Management {S.womanTipping}</div>
            When you subscribe to Slick, you don't just get the best software. You get the best support. Our goal is to
            help you use the software to achieve your business goals. We're with you every step of the way!
          </div>
          <div className={CN.heroImgContainer}>
            <div className={CN.bannerGradient} />
          </div>
        </div>

        <Title tag="SupportPage" img={IMG_CUSTOMER} text="Customer Support" />

        <div
          data-bem="SupportPage__support-card"
          className={cn({
            [CN.bannerCard]: true,
            [CN.withGradient]: true,
          })}
        >
          <div
            className={cn({
              [CN.contentBox]: true,
              [CN.inverted]: true,
            })}
          >
            We are the 'how to' team for all of your daily queries – "How to add a column" or “How to check out a
            client?” Something not working? Speak to us too!
          </div>
          <h2 className={CN.subTitle}>{S.raisedHands} Quickest way to contact us:</h2>
          <div
            className={cn({
              [CN.contactMethod]: true,
              [CN.byChat]: true,
            })}
          >
            <div className={CN.iconHelp}>
              <img src={IMG_HELP_CENTER} alt="Help center" className={CN.helpImg} />
              <h3>
                Live Chat • 7 days a week
                <span className={CN.subInfo}>2 min response time</span>
              </h3>
            </div>
            <div className={CN.qrCodeInfo}>
              <ResponsiveBox.Desktop>
                <div className={CN.qrCodeText}>
                  <div className={CN.smallText}>Use our chat bubble in the corner OR scan the QR code for WhatsApp</div>
                  <img src={IMG_QR_CODE} alt="QR code" className={CN.qrCode} />
                </div>
              </ResponsiveBox.Desktop>
              <ResponsiveBox.Mobile>
                <div className={CN.qrCodeText}>
                  <div className={CN.smallText}>
                    <Space>Use our chat bubble in the corner OR new</Space>
                    <ExternalLink link={LINK_WHATSAPP} variant="inline">
                      <div className={CN.link}> Whatsapp service</div>
                    </ExternalLink>
                  </div>
                </div>
              </ResponsiveBox.Mobile>
            </div>
          </div>
          <h2 className={CN.subTitle}>{S.megaphone} Alternative ways to contact us:</h2>
          <div className={CN.contactOptions}>
            <div className={CN.contactMethod}>
              <div className={CN.iconsContainer}>
                <img src={IMG_ENVELOPE} alt="Envelop" className={CN.icon} />
              </div>
              <h3>
                Email • 5 days a week
                <span className={CN.subInfo}>24 - 48 hrs response time</span>
                <ExternalLink link={LINK_MAIL} variant="inline">
                  <span className={CN.mail}>support@getslick.com</span>
                </ExternalLink>
              </h3>
            </div>
            <div className={CN.contactMethod}>
              <div className={CN.iconsContainer}>
                <img src={IMG_PHONE} alt="Phone" className={CN.icon} />
              </div>
              <h3>
                UK Phone • 5 days a week
                <span className={CN.subInfo}>30 - 60 mins response time</span>
                <ExternalLink link={LINK_PHONE} variant="inline">
                  <span className={CN.pmTime}>9am - 5pm •</span> <span className={CN.mail}> 01202 286 005</span>
                </ExternalLink>
              </h3>
            </div>
          </div>
        </div>

        <div data-bem="SupportPage__panel-product-features" className={CN.panelProductFeatures}>
          <div className={CN.imgPanelContainer}>
            <img src={IMG_PLAN} alt="Phone" className={CN.imgPanel} />
          </div>
          <div className={CN.textContainer}>
            <div className={CN.textBolder}>
              <ExternalLink link={LINK_ROADMAP} variant="inline">
                <span className={CN.linkBold}>View product features</span>
              </ExternalLink>
              <Space>that are coming soon!</Space>
            </div>

            <ResponsiveBox.Desktop>
              <div>
                <Space>Vote for upcoming features and</Space>
                <ExternalLink link={LINK_PLAN} variant="inline">
                  <span className={CN.link}>
                    <Space>leave feedback </Space>
                  </span>
                </ExternalLink>
                on existing ones
              </div>
            </ResponsiveBox.Desktop>
          </div>
          <div className={CN.panelButtonContainer}>
            <ExternalLink link={LINK_PLAN} variant="inline">
              <img src={IMG_BUTTON} alt="Phone" className={CN.imgButton} />
            </ExternalLink>
          </div>
        </div>

        <Title tag="SupportPage" img={IMG_MANAGEMENT} text="Account Management" />

        <div data-bem="SupportPage__account-manager" className={CN.bannerCard}>
          <div className={CN.topSection}>
            <div className={CN.leftBox}>
              <h2 className={CN.subTitle}>How will my account manager help?</h2>
              <div className={CN.contentBox}>
                <h3 className={CN.textTitle}>Product Training</h3>
                Get started with a new feature. Book in some time to cover all your questions at once!
                <h3 className={CN.textTitle}>Business coaching</h3>
                Want to earn more? What's happening in the industry? We're here to advise.
              </div>
            </div>

            <div className={CN.rightBox}>
              <h2 className={CN.subTitle}>Meet the team</h2>

              <div className={CN.videoThumbnail}>
                {RENDER_VIDEO ? (
                  <Video src={LINK_VIDEO} title={TITLE_VIDEO} />
                ) : (
                  <img src={IMG_TEAM} alt="Team" className={CN.videoImage} />
                )}
              </div>
            </div>
          </div>

          <div className={CN.bottomSection}>
            <h2 className={CN.subTitle}>How do I book in?</h2>
            <div className={CN.contentBox}>
              <h3 className={CN.textTitle}>Bookable via link only!</h3>
              These slots are bookable in advance (just like a client would book with you!)
            </div>
          </div>
        </div>

        {renderManagerSection && (
          <div data-bem="SupportPage__book-call" className={CN.bannerBook}>
            <div className={CN.avatarContainer}>
              <div className={CN.profileImgContainer}>
                <img src={image} alt="hone" className={CN.avatar} />
              </div>
              <div className={CN.userInfo}>
                <div>
                  <span className={CN.userRole}>Your Account Manager</span>
                </div>
                <span className={CN.userName}>{name}</span>
              </div>
            </div>

            <ExternalLink link={link} variant="inline">
              <div className={CN.bookButtonContainer}>
                <Icon variant="phoneOutline" width="lg" />
                <span className={CN.bookButtonText}>BOOK A CALL</span>
              </div>
            </ExternalLink>
          </div>
        )}

        <Title tag="SupportPage" img={IMG_TIPS} text="Support at your fingers tips" />

        <div data-bem="SupportPage__showcase" className={CN.bannerCard}>
          <h2 className={CN.subTitle}>Check out our broad range of educational content</h2>
          <Showcase tag="SupportPage" />
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
